import Gettext from 'node-gettext'

import { getLogger } from '~/utils/logger'

import { LocaleEnum } from '~/app/generated/graphql'
import localeModules from '~/locale/dist'

import { t, tn, tnp, tp } from './translators'

const inDebugMode = process.env.NEXT_ENV !== 'production'

const logDebug = getLogger('locale:gt', 'debug')
const logWarn = getLogger('locale:gt', 'warn')

function getSupportedLocales() {
  return Object.keys(localeModules)
}

type LocaleMessages = Record<string, string>

export class GT {
  private gettext: Gettext

  constructor(debug = inDebugMode) {
    this.gettext = new Gettext({ debug, sourceLocale: 'pl' })
  }

  get locale() {
    return this.gettext?.locale as LocaleEnum
  }

  t = (message: string, scope?: any) => t(this.gettext.gettext.bind(this.gettext))(message, scope)

  tn = (one: string, other: string, count: number, scope?: any) => tn(this.gettext.ngettext.bind(this.gettext))(one, other, count, scope)

  tp = (context: string, message: string, scope?: any) => tp(this.gettext.pgettext.bind(this.gettext))(context, message, scope)

  tnp = (context: string, one: string, other: string, count: number, scope?: any) =>
    tnp(this.gettext.npgettext.bind(this.gettext))(context, one, other, count, scope)

  getSupportedLocales() {
    return getSupportedLocales()
  }

  update(locale: string, messages: Record<string, string>) {
    logDebug(`Update locale ${locale}`)
    const { gettext } = this
    if (!gettext) {
      return
    }

    if (gettext.locale !== locale) {
      logDebug(`Detected locale change from ${gettext.locale || 'unknown'} to ${locale}`)
      Object.keys(messages).forEach(lang => {
        gettext.addTranslations(lang, 'messages', messages[lang] as unknown as object)
      })
      gettext.setLocale(locale)
    }
  }
}

const clientGt = new GT()
const serverGtByLocale = new Map<string, GT>()

const normalizeLocale = (locale?: string | null) => {
  const normalized = String(locale || 'en').toLowerCase()

  return getSupportedLocales().includes(normalized) ? normalized : 'en'
}

const getRequestLocaleFromHeaders = () => {
  if (typeof window !== 'undefined') {
    return null
  }

  try {
    // Next 16 removed sync headers(); read the request store it wraps directly. Avoid importing next internals in client bundles.
    const { workUnitAsyncStorage } =
      require('next/dist/server/app-render/work-unit-async-storage.external') as typeof import('next/dist/server/app-render/work-unit-async-storage.external')
    const { HEADERS } = require('../../utils/headers') as typeof import('../../utils/headers')

    const store = workUnitAsyncStorage.getStore()

    if (store?.type !== 'request') {
      return null
    }

    return store.headers.get(HEADERS.X_LANGUAGE)
  } catch (err) {
    logDebug(`getRequestLocaleFromHeaders failed unexpectedly: ${err}`)
    return null
  }
}

const getServerMessages = (locale: string): LocaleMessages => {
  switch (locale) {
    case 'pl':
      return require('../../locale/dist/pl.json')
    case 'cs':
      return require('../../locale/dist/cs.json')
    case 'de':
      return require('../../locale/dist/de.json')
    case 'uk':
      return require('../../locale/dist/uk.json')
    default:
      return require('../../locale/dist/en.json')
  }
}

const getServerGT = (locale: string) => {
  const normalizedLocale = normalizeLocale(locale)
  const cached = serverGtByLocale.get(normalizedLocale)

  if (cached) {
    return cached
  }

  const scopedGt = new GT()
  scopedGt.update(normalizedLocale, getServerMessages(normalizedLocale))
  serverGtByLocale.set(normalizedLocale, scopedGt)

  return scopedGt
}

const getActiveGT = () => {
  if (typeof window === 'undefined') {
    const locale = getRequestLocaleFromHeaders()

    if (!locale) {
      logWarn('x-language header missing in server context — falling back to "en". Check middleware matcher.')
    }

    return getServerGT(locale || 'en')
  }

  return clientGt
}

export const gt = new Proxy({} as GT, {
  get(_target, property) {
    const activeGT = getActiveGT()
    const value = activeGT[property as keyof GT]

    return typeof value === 'function' ? value.bind(activeGT) : value
  },
}) as GT
